function escapeHtml (value) {
  if (value === null || value === undefined) return ''
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

const STAR_FULL = '<svg width="100%" height="100%" viewBox="0 0 24 24" fill="currentColor" class="w-full h-full" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" clip-rule="evenodd" d="M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.007 5.404.433c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.433 2.082-5.006z"/></svg>'

function starRowHtml (rating, size = 'w-4 h-4') {
  const full = Math.max(0, Math.min(5, Math.floor(rating)))
  const empty = 5 - full
  let html = ''
  for (let i = 0; i < full; i += 1) {
    html += `<span class="inline-block ${size}">${STAR_FULL}</span>`
  }
  for (let i = 0; i < empty; i += 1) {
    html += `<span class="inline-block ${size} text-clay">${STAR_FULL}</span>`
  }
  return html
}

const CHECK_ICON = '<svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" class="w-full h-full" xmlns="http://www.w3.org/2000/svg"><polyline points="20 6 9 17 4 12"/></svg>'

const X_ICON = '<svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="w-full h-full" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12"/></svg>'

const CLOSE_BUTTON_CLASSES = 'tb-icon shrink-0 text-xl leading-none w-9 h-9 p-1 flex items-center justify-center bg-oat backdrop-blur-sm rounded-lg text-orange'

const CHEVRON_LEFT_ICON = '<svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" class="w-full h-full" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5"/></svg>'

const CHEVRON_RIGHT_ICON = '<svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" class="w-full h-full" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5"/></svg>'

const ARROW_RIGHT_ICON = '<svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" class="w-full h-full" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"/></svg>'

const GALLERY_I18N_FALLBACKS = {
  verifiedBuyer: 'Verified buyer',
  giftedProduct: 'Gifted product',
  galleryHeading: 'Customer photos ([[count]])',
  galleryAllPhotos: 'All photos ([[count]])',
  galleryCounter: '[[current]] of [[total]]',
  galleryReadFull: 'Read full review',
  galleryPrevAria: 'Previous photo',
  galleryNextAria: 'Next photo',
  galleryCloseAria: 'Close',
  galleryOpenPhotoAria: 'Open photo [[index]]',
  galleryLoading: 'Loading photos…',
  pillCount: '[[title]] ([[count]])',
  defaultAuthor: 'Verified buyer',
  errorLabel: 'Could not load photos.',
  errorRetry: 'Try again',
  loadMoreLabel: 'Load more',
}

function interpolate (template, values) {
  if (!template) return ''
  return String(template).replace(/\[\[(\w+)\]\]/g, (_, key) => {
    const value = values ? values[key] : undefined
    if (value === undefined || value === null) return ''
    return String(value)
  })
}

function formatDate (iso) {
  if (!iso) return ''
  try {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return ''
    return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
  } catch {
    return ''
  }
}

class TbReviewsGallery extends HTMLElement {
  connectedCallback () {
    this.appUrl = (this.dataset.appUrl || '').replace(/\/$/, '')
    this.store = this.dataset.store || ''
    this.productId = this.dataset.productId || ''
    this.photoTotal = parseInt(this.dataset.photoTotal || '0', 10) || 0
    this._photoCache = new Map()
    this._built = false
    this.isOpen = false
    this.view = 'grid'
    this.component = 'all'
    this.index = 0
    this.localPhotos = null
    this.handleKeydown = this.handleKeydown.bind(this)
  }

  disconnectedCallback () {
    document.removeEventListener('keydown', this.handleKeydown)
    if (this._overlayRoot && !this.isConnected) {
      this._overlayRoot.remove()
      this._overlayRoot = null
    }
  }

  get overlayRoot () {
    if (!this._overlayRoot || !this._overlayRoot.isConnected) {
      this._overlayRoot = document.createElement('div')
      this._overlayRoot.className = 'tb-reviews-gallery-overlay'
      document.body.appendChild(this._overlayRoot)
      this.bindOverlayEvents(this._overlayRoot)
    }
    return this._overlayRoot
  }

  get host () {
    return this.closest('tb-product-reviews')
  }

  ensureConfig () {
    if (this._config) return this._config
    let config = {}
    const configEl = this.host?.querySelector('script[type="application/json"][data-reviews-config]')
    if (configEl) {
      try {
        config = JSON.parse(configEl.textContent || '{}')
      } catch {
        config = {}
      }
    }
    this._config = config
    this.i18n = { ...GALLERY_I18N_FALLBACKS, ...(config.i18n || {}) }
    this.byComponent = Array.isArray(config.byComponent) ? config.byComponent : []
    // Mirrors tb-product-reviews.js: product tags only when reviews are pooled.
    this.hasComponents = this.byComponent.some(comp => comp && comp.title)
    this.productTitle = config.productTitle || ''
    return config
  }

  cacheFor (component) {
    const key = component || 'all'
    if (!this._photoCache.has(key)) {
      this._photoCache.set(key, { photos: [], cursor: null, exhausted: false, total: null, loading: false, error: false, inflight: null, seeded: false })
    }
    return this._photoCache.get(key)
  }

  buildPhotosUrl (component, cursor) {
    const params = new URLSearchParams()
    params.set('store', this.store)
    params.set('limit', '12')
    if (cursor) params.set('cursor', cursor)
    if (component && component !== 'all') params.set('component', component)
    return `${this.appUrl}/api/storefront/klaviyo-reviews/${encodeURIComponent(this.productId)}/photos?${params.toString()}`
  }

  seedLocalCache (component) {
    const cache = this.cacheFor(component)
    if (cache.seeded) return
    cache.seeded = true
    const host = this.host
    const rows = [...(host?.seedReviews || []), ...(host?.apiReviews || [])]
    const target = String(component || 'all').toLowerCase()
    for (const review of rows) {
      if (!Array.isArray(review.images) || !review.images.length) continue
      const comp = String(review.bundle_component_title || '').toLowerCase()
      if (target !== 'all') {
        if (target === 'self' ? comp : comp !== target) continue
      }
      for (const img of review.images) {
        const full = img?.full || img?.thumb
        if (!full) continue
        cache.photos.push({
          thumb: img.thumb || full,
          full,
          review: {
            id: review.id,
            title: review.title,
            excerpt: String(review.content || '').slice(0, 220),
            rating: review.rating,
            author: review.author,
            created: review.created,
            verified: review.verified,
            gifted: review.gifted === true,
            bundle_component_title: review.bundle_component_title || null, // eslint-disable-line camelcase
          },
        })
      }
    }
    cache.total = cache.photos.length
    cache.exhausted = true
  }

  fetchPhotos (component, { silent = false } = {}) {
    const cache = this.cacheFor(component)
    if (cache.inflight) return cache.inflight
    if (cache.exhausted || cache.error) return Promise.resolve(false)
    if (!this.appUrl || !this.store || !this.productId) {
      this.seedLocalCache(component)
      this.renderIfOpen()
      return Promise.resolve(false)
    }
    cache.loading = true
    if (!silent) this.renderIfOpen()
    cache.inflight = (async () => {
      try {
        const res = await fetch(this.buildPhotosUrl(component, cache.cursor), {
          headers: { Accept: 'application/json' },
          credentials: 'omit',
        })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const json = await res.json()
        const incoming = Array.isArray(json.photos) ? json.photos : []
        const seen = new Set(cache.photos.map(p => p.full))
        for (const photo of incoming) {
          if (photo && photo.full && !seen.has(photo.full)) {
            seen.add(photo.full)
            cache.photos.push(photo)
          }
        }
        cache.total = Number.isFinite(json.total_photos) ? json.total_photos : cache.photos.length
        cache.cursor = json.next_cursor || null
        cache.exhausted = !cache.cursor
        return true
      } catch (err) {
        console.warn('[tb-reviews-gallery] photos fetch failed', err)
        cache.error = true
        return false
      } finally {
        cache.loading = false
        cache.inflight = null
        this.renderIfOpen()
      }
    })()
    return cache.inflight
  }

  currentPhotos () {
    if (this.localPhotos) return this.localPhotos
    return this.cacheFor(this.component).photos
  }

  currentTotal () {
    if (this.localPhotos) return this.localPhotos.length
    const cache = this.cacheFor(this.component)
    if (cache.total !== null) return cache.total
    if (this.component === 'all') return this.photoTotal
    const comp = this.byComponent.find(c => (c.title || 'self') === this.component || c.title === this.component)
    return comp?.photos ?? cache.photos.length
  }

  openGallery ({ view = 'grid', photoFull = '', review = null, component = 'all' } = {}) {
    this.ensureConfig()
    this.view = view
    this.component = component || 'all'
    this.localPhotos = null
    this.index = 0
    this._returnFocus = document.activeElement

    if (view === 'grid') {
      this.fetchPhotos(this.component)
    } else {
      const cache = this.cacheFor('all')
      const located = photoFull ? cache.photos.findIndex(p => p.full === photoFull) : -1
      if (located >= 0) {
        this.component = 'all'
        this.index = located
      } else if (review && Array.isArray(review.images) && review.images.length) {
        this.localPhotos = review.images.map(img => ({
          thumb: img.thumb || img.full,
          full: img.full || img.thumb,
          review: {
            id: review.id,
            title: review.title,
            excerpt: String(review.content || '').slice(0, 220),
            rating: review.rating,
            author: review.author,
            created: review.created,
            verified: review.verified,
            gifted: review.gifted === true,
            bundle_component_title: review.bundle_component_title || null, // eslint-disable-line camelcase
          },
        }))
        const localIndex = this.localPhotos.findIndex(p => p.full === photoFull)
        this.index = localIndex >= 0 ? localIndex : 0
        this._pendingLocate = photoFull
        this.fetchPhotos('all').then(() => this.tryPromoteToGlobal())
      } else {
        this.component = 'all'
        this._pendingLocate = photoFull
        this.fetchPhotos('all').then(() => this.tryPromoteToGlobal())
      }
    }

    this.isOpen = true
    window.dispatchEvent(new CustomEvent('show:page-obscure'))
    window.dispatchEvent(new CustomEvent('prevent:page-scroll'))
    document.addEventListener('keydown', this.handleKeydown)
    this.render()
    window.requestAnimationFrame(() => {
      this.overlayRoot.querySelector('[data-gallery-close]')?.focus({ preventScroll: true })
    })
  }

  tryPromoteToGlobal (attempts = 0) {
    if (!this.isOpen || !this._pendingLocate) return
    const cache = this.cacheFor('all')
    const located = cache.photos.findIndex(p => p.full === this._pendingLocate)
    if (located >= 0) {
      this.localPhotos = null
      this.component = 'all'
      this.index = located
      this._pendingLocate = null
      this.render()
      return
    }
    if (cache.exhausted || cache.error || attempts >= 4) {
      this._pendingLocate = null
      return
    }
    this.fetchPhotos('all').then(progressed => {
      if (progressed) {
        this.tryPromoteToGlobal(attempts + 1)
      } else {
        this._pendingLocate = null
      }
    })
  }

  close () {
    this.isOpen = false
    if (this._overlayRoot) this._overlayRoot.innerHTML = ''
    window.dispatchEvent(new CustomEvent('hide:page-obscure'))
    window.dispatchEvent(new CustomEvent('allow:page-scroll'))
    document.removeEventListener('keydown', this.handleKeydown)
    if (this._returnFocus && typeof this._returnFocus.focus === 'function') {
      this._returnFocus.focus({ preventScroll: true })
    }
  }

  handleKeydown (event) {
    if (!this.isOpen) return
    if (event.key === 'Escape') {
      this.close()
    } else if (event.key === 'Tab') {
      this.trapFocus(event)
    } else if (this.view !== 'grid') {
      if (event.key === 'ArrowLeft') this.navigate(-1)
      else if (event.key === 'ArrowRight') this.navigate(1)
    }
  }

  trapFocus (event, container = this._overlayRoot?.querySelector('[data-gallery-panel]')) {
    if (!container) return
    const focusables = container.querySelectorAll('button:not([disabled]), input, select, a[href], [tabindex]:not([tabindex="-1"])')
    if (!focusables.length) return
    const first = focusables[0]
    const last = focusables[focusables.length - 1]
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault()
      last.focus()
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault()
      first.focus()
    } else if (!container.contains(document.activeElement)) {
      event.preventDefault()
      first.focus()
    }
  }

  navigate (delta) {
    const photos = this.currentPhotos()
    if (!photos.length) return
    const total = this.currentTotal()
    let next = this.index + delta
    if (next < 0) next = 0
    if (next > photos.length - 1) {
      const cache = this.localPhotos ? null : this.cacheFor(this.component)
      if (cache && !cache.exhausted && next < total) {
        this.fetchPhotos(this.component).then(() => {
          if (this.currentPhotos().length > this.index) this.navigate(delta)
        })
      }
      next = photos.length - 1
    }
    if (next !== this.index) {
      this.index = next
      this.render()
    }
  }

  renderIfOpen () {
    if (this.isOpen) this.render()
  }

  isDesktop () {
    return window.matchMedia('(min-width: 768px)').matches
  }

  render () {
    if (!this.isOpen) return
    const root = this.overlayRoot
    const prevScroll = this.view === 'grid' ? (root.querySelector('[data-gallery-scroll]')?.scrollTop ?? 0) : 0
    if (this.view === 'grid') {
      root.innerHTML = this.gridHtml()
      const scroller = root.querySelector('[data-gallery-scroll]')
      if (scroller) {
        scroller.scrollTop = prevScroll
        scroller.addEventListener('scroll', () => this.updateGridScrollState(scroller), { passive: true })
        this.updateGridScrollState(scroller)
      }
    } else if (this.view === 'detail' && this.isDesktop()) {
      root.innerHTML = this.detailHtml()
    } else {
      root.innerHTML = this.lightboxHtml()
    }
    if (this.view !== 'grid') {
      const img = root.querySelector('[data-gallery-image]')
      if (img) {
        const reveal = () => {
          img.classList.remove('opacity-0')
          root.querySelector('[data-gallery-spinner]')?.classList.add('hidden')
        }
        if (img.complete && img.naturalWidth > 0) reveal()
        else {
          img.addEventListener('load', reveal, { once: true })
          img.addEventListener('error', reveal, { once: true })
        }
      }
    }
    if (!root.contains(document.activeElement)) {
      root.querySelector('[data-gallery-close]')?.focus({ preventScroll: true })
    }
  }

  updateGridScrollState (scroller) {
    const root = this._overlayRoot
    if (!root) return
    const maxScroll = scroller.scrollHeight - scroller.clientHeight
    const atTop = scroller.scrollTop <= 1
    const atBottom = scroller.scrollTop >= maxScroll - 1
    root.querySelector('[data-gallery-fader="top"]')?.classList.toggle('opacity-0', atTop)
    root.querySelector('[data-gallery-fader="bottom"]')?.classList.toggle('opacity-0', atBottom || maxScroll <= 0)
    const cache = this.cacheFor(this.component)
    if (maxScroll > 0 && maxScroll - scroller.scrollTop < 600 && !cache.loading && !cache.exhausted && !cache.error) {
      this.fetchPhotos(this.component, { silent: true })
    }
  }

  bindOverlayEvents (root) {
    root.addEventListener('click', (event) => {
      if (!this.isOpen) return

      if (event.target.closest('[data-gallery-close]')) {
        this.close()
        return
      }

      const pill = event.target.closest('[data-gallery-pill]')
      if (pill) {
        this.component = pill.dataset.galleryPill || 'all'
        this.fetchPhotos(this.component)
        this.render()
        return
      }

      const thumb = event.target.closest('[data-gallery-photo]')
      if (thumb) {
        this.localPhotos = null
        this.index = parseInt(thumb.dataset.galleryPhoto || '0', 10) || 0
        this.view = this.isDesktop() ? 'detail' : 'lightbox'
        this.render()
        return
      }

      if (event.target.closest('[data-gallery-prev]')) {
        this.navigate(-1)
        return
      }

      if (event.target.closest('[data-gallery-next]')) {
        this.navigate(1)
        return
      }

      const readFull = event.target.closest('[data-read-full]')
      if (readFull) {
        const photos = this.currentPhotos()
        const photo = photos[this.index]
        const host = this.host
        this.close()
        if (photo?.review && host && typeof host.showReview === 'function') {
          const existing = typeof host.findReviewById === 'function' ? host.findReviewById(photo.review.id) : null
          const fallback = { ...photo.review, content: photo.review.content || photo.review.excerpt || '' }
          host.showReview(existing || fallback)
        }
        return
      }

      const retry = event.target.closest('[data-gallery-retry]')
      if (retry) {
        this.cacheFor(this.component).error = false
        this.fetchPhotos(this.component)
        return
      }

      const loadMore = event.target.closest('[data-gallery-load-more]')
      if (loadMore) {
        this.fetchPhotos(this.component)
      }
    })

    document.querySelector('#tb_page_obscure')?.addEventListener('click', () => {
      if (this.isOpen) this.close()
    })

    window.addEventListener('hide:page-obscure', () => {
      if (this.isOpen) this.close()
    })
  }

  pillRowHtml (useGridCounts) {
    const hasComponents = this.byComponent.some(c => c.title !== null && c.title !== '' && (c.photos ?? 0) > 0)
    if (!hasComponents) return ''
    const allActive = this.component === 'all'
    const allCount = useGridCounts ? (this.cacheFor('all').total ?? this.photoTotal) : this.photoTotal
    const pillClass = (active) => `reviews-gallery__pill${active ? ' reviews-gallery__pill--active' : ''} shrink-0 px-3.5 py-1.5 rounded-full text-smol font-medium whitespace-nowrap ${active ? 'bg-black text-white' : 'bg-white text-black border border-clay'}`
    let html = `<button type="button" class="${pillClass(allActive)}" data-gallery-pill="all" aria-pressed="${allActive}">${escapeHtml(interpolate(this.i18n.galleryAllPhotos, { count: allCount }))}</button>`
    for (const comp of this.byComponent) {
      const photos = comp.photos ?? 0
      if (!photos) continue
      const value = comp.title === null || comp.title === '' ? 'self' : comp.title
      const title = comp.title === null || comp.title === '' ? this.productTitle : comp.title
      const active = this.component === value
      html += `<button type="button" class="${pillClass(active)}" data-gallery-pill="${escapeHtml(value)}" aria-pressed="${active}">${escapeHtml(interpolate(this.i18n.pillCount, { title, count: photos }))}</button>`
    }
    return html
  }

  gridHtml () {
    const cache = this.cacheFor(this.component)
    const total = this.currentTotal()
    const heading = interpolate(this.i18n.galleryHeading, { count: total })
    const closeAria = escapeHtml(this.i18n.galleryCloseAria)

    const thumbs = cache.photos.map((photo, i) => {
      const aria = interpolate(this.i18n.galleryOpenPhotoAria, { index: i + 1 })
      return `<button type="button" class="reviews-gallery__thumb relative block w-full aspect-square rounded-md overflow-hidden focus:outline-hidden focus:ring-2 focus:ring-black" data-gallery-photo="${i}" aria-label="${escapeHtml(aria)}"><img src="${escapeHtml(photo.thumb)}" alt="" loading="lazy" decoding="async" width="300" height="300" class="reviews-gallery__thumb-img w-full h-full object-cover"></button>`
    }).join('')

    const loadingHtml = cache.loading
      ? `<div class="reviews-gallery__loading col-span-full text-center text-iron text-sm py-4">${escapeHtml(this.i18n.galleryLoading)}</div>`
      : ''
    const errorHtml = cache.error && !cache.loading
      ? `<div class="reviews-gallery__error col-span-full flex flex-col items-center gap-2 py-4 text-sm text-iron">${escapeHtml(this.i18n.errorLabel)}<button type="button" class="reviews-gallery__retry underline hover:text-black" data-gallery-retry>${escapeHtml(this.i18n.errorRetry)}</button></div>`
      : ''
    const loadMoreHtml = !cache.loading && !cache.exhausted && !cache.error
      ? `<div class="reviews-gallery__load-more-wrap col-span-full flex justify-center py-2"><button type="button" class="reviews-gallery__load-more px-4 py-2 text-sm border border-clay rounded-lg" data-gallery-load-more>${escapeHtml(this.i18n.loadMoreLabel)}</button></div>`
      : ''

    const pillRow = this.pillRowHtml(true)

    return `
      <div class="reviews-gallery fixed inset-0 z-[10050] pointer-events-none">
        <div class="reviews-gallery__panel pointer-events-auto absolute inset-0 md:inset-auto md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2 md:w-[960px] md:max-w-[95vw] md:max-h-[85vh] bg-white md:rounded-lg md:shadow-[0px_2px_8px_0px_rgba(20,15,10,0.06),0px_8px_24px_0px_rgba(20,15,10,0.08)] flex flex-col gap-4 md:gap-5 p-5 md:p-8 overflow-hidden" data-gallery-panel role="dialog" aria-modal="true" aria-label="${escapeHtml(heading)}">
          <button type="button" class="reviews-gallery__close absolute top-2.5 right-2.5 md:top-6 md:right-6 z-10 ${CLOSE_BUTTON_CLASSES}" data-gallery-close aria-label="${closeAria}">${X_ICON}</button>
          <div class="reviews-gallery__header flex items-center shrink-0">
            <p class="reviews-gallery__heading text-lg md:text-xl font-semibold">${escapeHtml(heading)}</p>
          </div>
          ${pillRow ? `<div class="reviews-gallery__pills flex gap-2 items-start overflow-x-auto product-reviews__hide-scrollbar shrink-0">${pillRow}</div>` : ''}
          <div class="reviews-gallery__scroll-frame relative flex flex-col flex-grow min-h-0">
            <div class="reviews-gallery__scroll flex-1 min-h-0 overflow-y-auto overscroll-contain" data-gallery-scroll>
              <div class="reviews-gallery__grid grid grid-cols-3 md:grid-cols-6 gap-3">
                ${thumbs}
                ${loadingHtml}
                ${errorHtml}
                ${loadMoreHtml}
              </div>
            </div>
            <div class="reviews-gallery__fader reviews-gallery__fader--top absolute -top-px inset-x-0 h-8 bg-gradient-to-b from-white to-transparent pointer-events-none opacity-0 transition-opacity duration-200" data-gallery-fader="top"><span class="sr-only">More content above</span></div>
            <div class="reviews-gallery__fader reviews-gallery__fader--bottom absolute -bottom-px inset-x-0 h-8 bg-gradient-to-t from-white to-transparent pointer-events-none opacity-0 transition-opacity duration-200" data-gallery-fader="bottom"><span class="sr-only">More content below</span></div>
          </div>
        </div>
      </div>`
  }

  viewerPhoto () {
    const photos = this.currentPhotos()
    return photos[Math.min(this.index, photos.length - 1)] || null
  }

  counterHtml () {
    const total = this.currentTotal()
    const text = interpolate(this.i18n.galleryCounter, { current: this.index + 1, total })
    return `<span class="reviews-gallery__counter absolute bottom-5 left-1/2 -translate-x-1/2 bg-white px-3 py-1.5 rounded-full text-smol font-medium">${escapeHtml(text)}</span>`
  }

  navButtonsHtml () {
    const prevAria = escapeHtml(this.i18n.galleryPrevAria)
    const nextAria = escapeHtml(this.i18n.galleryNextAria)
    const btnClass = 'absolute top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white border border-clay shadow-c flex items-center justify-center'
    return `
      <button type="button" class="reviews-gallery__nav reviews-gallery__nav--prev ${btnClass} left-2 md:left-5" data-gallery-prev aria-label="${prevAria}"><span class="tb-icon w-5 h-5 -translate-x-px">${CHEVRON_LEFT_ICON}</span></button>
      <button type="button" class="reviews-gallery__nav reviews-gallery__nav--next ${btnClass} right-2 md:right-5" data-gallery-next aria-label="${nextAria}"><span class="tb-icon w-5 h-5 translate-x-px">${CHEVRON_RIGHT_ICON}</span></button>`
  }

  reviewMetaHtml (review) {
    if (!review) return { verified: '', tag: '', stars: '' }
    let verified = ''
    if (review.gifted === true) {
      verified = `<span class="reviews-gallery__verified reviews-gallery__verified--gifted select-none inline-flex items-center text-xs leading-[1.1] text-iron bg-oat border border-clay rounded-full px-2 py-1">${escapeHtml(this.i18n.giftedProduct)}</span>`
    } else if (review.verified) {
      verified = `<span class="reviews-gallery__verified select-none inline-flex items-center gap-1 text-xs text-white bg-success rounded-full px-2 py-1"><span class="tb-icon inline-block w-3 h-3">${CHECK_ICON}</span>${escapeHtml(this.i18n.verifiedBuyer)}</span>`
    }
    const tagLabel = this.hasComponents ? (review.bundle_component_title || this.productTitle) : ''
    const tag = tagLabel
      ? `<span class="reviews-gallery__tag inline-flex items-center justify-center p-1 text-xs bg-white border border-clay rounded">${escapeHtml(tagLabel)}</span>`
      : ''
    const stars = `<div class="reviews-gallery__stars flex items-center gap-0.5 text-gold" role="img" aria-label="${escapeHtml(String(review.rating || 0))}">${starRowHtml(Number(review.rating) || 0, 'w-4.5 h-4.5')}</div>`
    return { verified, tag, stars }
  }

  detailHtml () {
    const photo = this.viewerPhoto()
    if (!photo) return this.gridHtml()
    const review = photo.review || {}
    const { verified, tag, stars } = this.reviewMetaHtml(review)
    const closeAria = escapeHtml(this.i18n.galleryCloseAria)
    const author = review.author || this.i18n.defaultAuthor
    const excerpt = review.excerpt || ''

    return `
      <div class="reviews-gallery fixed inset-0 z-[10050] pointer-events-none">
        <div class="reviews-gallery__panel pointer-events-auto absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[1100px] max-w-[95vw] h-[640px] max-h-[85vh] bg-white rounded-lg shadow-[0px_2px_8px_0px_rgba(20,15,10,0.06),0px_8px_24px_0px_rgba(20,15,10,0.08)] overflow-hidden flex" data-gallery-panel role="dialog" aria-modal="true">
          <div class="reviews-gallery__photo relative flex-1 bg-[#1d1d1d] min-w-0">
            <span class="reviews-gallery__spinner absolute inset-0 flex items-center justify-center pointer-events-none" data-gallery-spinner aria-hidden="true"><span class="inline-block w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin"></span></span>
            <img src="${escapeHtml(photo.full)}" alt="" class="reviews-gallery__photo-img absolute inset-0 w-full h-full object-contain opacity-0 transition-opacity duration-200" data-gallery-image>
            ${this.navButtonsHtml()}
            ${this.counterHtml()}
          </div>
          <div class="reviews-gallery__review relative w-[400px] shrink-0 bg-white flex flex-col gap-4 p-8 overflow-y-auto">
            <button type="button" class="reviews-gallery__close absolute top-4 right-4 ${CLOSE_BUTTON_CLASSES}" data-gallery-close aria-label="${closeAria}">${X_ICON}</button>
            <div class="reviews-gallery__stars-row flex items-center">
              ${stars}
            </div>
            <div class="reviews-gallery__tags flex items-center gap-2">
              ${verified}
              ${tag}
            </div>
            ${review.title ? `<p class="reviews-gallery__title text-lg font-semibold">${escapeHtml(review.title)}</p>` : ''}
            ${excerpt ? `<p class="reviews-gallery__excerpt text-sm text-iron">${escapeHtml(excerpt)}${excerpt.length >= 160 ? '…' : ''}</p>` : ''}
            <button type="button" class="reviews-gallery__read-full inline-flex items-center gap-1 self-start text-base hover:underline" data-read-full>
              ${escapeHtml(this.i18n.galleryReadFull)}
              <span class="tb-icon inline-block w-4 h-4" aria-hidden="true">${ARROW_RIGHT_ICON}</span>
            </button>
            <span class="reviews-gallery__spacer block flex-1"></span>
            <div class="reviews-gallery__footer flex items-center justify-between text-smol">
              <span class="reviews-gallery__author text-black-h">${escapeHtml(author)}</span>
              <span class="reviews-gallery__date text-iron">${escapeHtml(formatDate(review.created))}</span>
            </div>
          </div>
        </div>
      </div>`
  }

  lightboxHtml () {
    const photo = this.viewerPhoto()
    if (!photo) return this.gridHtml()
    const review = photo.review || {}
    const closeAria = escapeHtml(this.i18n.galleryCloseAria)
    const author = review.author || this.i18n.defaultAuthor
    const tagLabel = this.hasComponents ? (review.bundle_component_title || this.productTitle) : ''
    const caption = `"${review.title || ''}" — ${author}, ${formatDate(review.created)}`

    return `
      <div class="reviews-gallery fixed inset-0 z-[10050] pointer-events-none">
        <div class="reviews-gallery__panel pointer-events-auto absolute inset-x-4 top-1/2 -translate-y-1/2 md:inset-x-auto md:left-1/2 md:-translate-x-1/2 md:w-[760px] md:max-w-[90vw] bg-[#0d0d0d] rounded-lg shadow-[0px_12px_32px_0px_rgba(0,0,0,0.3)] overflow-hidden flex flex-col" data-gallery-panel role="dialog" aria-modal="true">
          <div class="reviews-gallery__photo relative w-full aspect-[358/377] md:aspect-[760/620] bg-[#0d0d0d]">
            <span class="reviews-gallery__spinner absolute inset-0 flex items-center justify-center pointer-events-none" data-gallery-spinner aria-hidden="true"><span class="inline-block w-8 h-8 border-2 border-white/30 border-t-white rounded-full animate-spin"></span></span>
            <img src="${escapeHtml(photo.full)}" alt="" class="reviews-gallery__photo-img absolute inset-0 w-full h-full object-contain opacity-0 transition-opacity duration-200" data-gallery-image>
            <button type="button" class="reviews-gallery__close absolute top-2.5 right-2.5 ${CLOSE_BUTTON_CLASSES}" data-gallery-close aria-label="${closeAria}">${X_ICON}</button>
            ${this.navButtonsHtml()}
            ${this.counterHtml()}
          </div>
          <div class="reviews-gallery__caption-bar bg-oat flex items-center justify-between px-6 py-5">
            <div class="reviews-gallery__caption-group flex flex-col gap-3 min-w-0">
              <div class="reviews-gallery__caption-meta flex items-center gap-3">
                ${tagLabel ? `<span class="reviews-gallery__tag reviews-gallery__tag--dark inline-flex items-center justify-center p-1 text-xs text-white bg-black-h rounded">${escapeHtml(tagLabel)}</span>` : ''}
                <div class="reviews-gallery__stars flex items-center gap-0.5 text-gold">${starRowHtml(Number(review.rating) || 0)}</div>
              </div>
              <p class="reviews-gallery__caption text-sm text-black">${escapeHtml(caption)}</p>
            </div>
          </div>
        </div>
      </div>`
  }
}

if (!customElements.get('tb-reviews-gallery')) {
  customElements.define('tb-reviews-gallery', TbReviewsGallery)
}
